import { action, computed, makeObservable } from 'mobx'
import type { MobxDocument } from '../firestore-mobx'
import {
  ObservableModelWithDecorators,
  emptyCollection,
  emptyModel,
} from '../firestore-mobx/model'
import {
  empty,
  type FirestoreSlideDeckExhibit,
} from '../firestore/SlideDeckExhibit'
import type { FirebaseRepository } from './FirebaseRepository'
import { fetchMedia } from '../firestore/Media'
import { Media } from './Media'
import { noTryAsync } from '../util'

export class SlideDeckExhibit extends ObservableModelWithDecorators<FirestoreSlideDeckExhibit> {
  media: Media | null = null

  static empty(repository: FirebaseRepository) {
    return emptyModel(repository, this, empty)
  }

  static emptyCollection(repository: FirebaseRepository) {
    return emptyCollection(repository, this, empty)
  }

  constructor(
    repository: FirebaseRepository,
    doc: MobxDocument<FirestoreSlideDeckExhibit>
  ) {
    super(repository, doc)

    makeObservable(this)
  }

  @computed
  get mediaId() {
    return this.data.mediaId
  }

  @computed
  get altText() {
    return (
      this.media?.data.mediaAltText ||
      this.data.exhibitAltText ||
      this.data.exhibitName
    )
  }

  @computed
  get thumbnailURL() {
    const { exhibitURL, mediaId, exhibitHasThumbnail, exhibitThumbnailURL } =
      this.data

    // Use pre-signed thumbnail URL from the API if available
    if (exhibitThumbnailURL) return exhibitThumbnailURL

    // if no thumbnail, use full res URL
    if (!exhibitHasThumbnail || !exhibitURL) return exhibitURL

    // if not using custom media, simple _thumbnail suffix
    if (!mediaId) return exhibitURL.replace(this.id, this.id + '_thumbnail')

    // media/{mediaId}/{mediaId}_thumbnail.jpg is the pattern (url encoded)
    // replace last mediaID with mediaID_thumbnail

    const lastIndexOfMediaId = exhibitURL.lastIndexOf(mediaId)
    if (lastIndexOfMediaId === -1) return exhibitURL

    return (
      exhibitURL.slice(0, lastIndexOfMediaId) +
      mediaId +
      '_thumbnail' +
      exhibitURL.slice(lastIndexOfMediaId + mediaId.length)
    )
  }

  @computed
  get inputSources() {
    return this.data.inputSources ?? []
  }

  @computed
  get hasInputSources() {
    return this.inputSources.length > 0
  }

  get hasMedia() {
    return !!this.data.mediaId
  }

  mediaLoadOngoing = false
  async loadMedia() {
    const mediaId = this.mediaId
    if (mediaId && !this.media && !this.mediaLoadOngoing) {
      this.mediaLoadOngoing = true

      await noTryAsync(async () => {
        const media = await fetchMedia(this.repository, { mediaId })

        this.setMedia(media)
      })

      this.mediaLoadOngoing = false
    }
  }

  @action
  setMedia(media: Media) {
    this.media = media
  }
}
